/* One price owner: exact configured offer totals, with real cart totals as a fallback. */
(() => {
  'use strict';

  function cartOfferTotal(cart, selectedIds, productIds) {
    if (!cart || !Array.isArray(cart.items) || !selectedIds.length) return null;
    const expected = new Map();
    for (const id of selectedIds) expected.set(String(id), (expected.get(String(id)) || 0) + 1);
    const actual = new Map();
    const ownIds = new Set(productIds.map(String));
    let total = 0;
    for (const item of cart.items) {
      const id = String(item.variant_id);
      if (!ownIds.has(id)) continue;
      if (!expected.has(id) || !Number.isSafeInteger(item.quantity) || item.quantity <= 0 ||
          !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0) return null;
      actual.set(id, (actual.get(id) || 0) + item.quantity);
      total += item.final_line_price;
    }
    if (expected.size !== actual.size || [...expected].some(([id, qty]) => actual.get(id) !== qty)) return null;
    return Number.isSafeInteger(total) ? total : null;
  }

  function offerValues(variants, ids, compareMode) {
    const selected = ids.map(id => variants.find(variant => String(variant.id) === String(id)));
    if (selected.some(variant => !variant)) return null;
    return selected.reduce((result, variant) => ({
      price: result.price + variant.price,
      compare: result.compare + (compareMode === 'compare_price' ? Math.max(variant.price, variant.compare_at_price || 0) : variant.price),
    }), {price:0, compare:0});
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = {cartOfferTotal, offerValues};
    return;
  }
  if (window.quantityCartPricesLoaded) return;
  window.quantityCartPricesLoaded = true;
  const instances = new Set();
  let latestCart = null;
  let refreshTimer;
  let requestVersion = 0;

  // Native quantity changes can leave formVariants empty when pack pickers are fixed.
  // Always supply the actual three variant IDs before the native product-form submits.
  function synchronizeGuideSelection(element) {
    if (!element?.dataset.guidePackIds) return;
    const input = element.querySelector('input[type="radio"]:checked');
    if (!input) return;
    const label = [...element.querySelectorAll('label.quantity-break')].find(label => label.dataset.input === input.dataset.input);
    let ids;
    if (Number(input.value) === 3) ids = JSON.parse(element.dataset.guidePackIds).map(String);
    else if (Number(input.value) === 1) {
      const id = label?.querySelector('.quantity-break__selector-item')?.dataset.selectedId;
      if (id) ids = [String(id)];
    }
    if (!ids || ids.length !== Number(input.value)) return;
    element.selectedVariants ||= {};
    element.selectedVariants[input.dataset.input] = [...ids];
    element.formVariants = [...ids];
    element.selectedQuantity = ids.length;
  }

  class CartPrices {
    constructor(element) {
      this.element = element;
      this.variants = JSON.parse(element.querySelector('script[data-has-variants]').textContent);
      this.cart = JSON.parse(element.querySelector('[data-selector-cart-prices]').textContent);
      if (latestCart?.currency === element.dataset.cartCurrency) this.cart = latestCart;
      this.live = element.dataset.liveShopifyPrices === 'true';
      this.quotes = new Map();
      if (this.live) {
        try {
          this.quoteClient = new window.ShopifyOfferQuote({
            endpoint:element.dataset.storefrontApiUrl, publicToken:element.dataset.storefrontPublicToken,
            country:element.dataset.storefrontCountry, currency:element.dataset.cartCurrency,
          });
        } catch {this.quoteClient = null;}
      }
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {this.frame = null; this.render();});
      };
      this.observer = new MutationObserver(this.schedule);
      // Observe variant IDs, never the price text we write ourselves.
      this.observer.observe(element, {subtree:true, attributes:true, attributeFilter:['data-selected-id']});
      element.addEventListener('change', this.schedule);
      element.addEventListener('click', this.schedule);
      this.render();
    }

    selectedIds(label) {
      const quantity = Number(label.dataset.quantity);
      const pickers = [...label.querySelectorAll('.quantity-break__selector-item')];
      if (pickers.length === quantity) return pickers.map(picker => picker.dataset.selectedId);
      const ids = this.element.selectedVariants?.[label.dataset.input];
      if (ids?.length === quantity) return ids;
      const form = document.getElementById('product-form-' + this.element.dataset.section);
      const selectedId = form?.querySelector('[name="id"]')?.value || this.variants[0]?.id;
      return Array(quantity).fill(selectedId);
    }

    liveQuote(ids) {
      if (!this.quoteClient) return {failed:true};
      const key = [...ids].map(String).sort().join(',');
      let quote = this.quotes.get(key);
      if (!quote) {quote={value:null, at:0, pending:false, failed:false}; this.quotes.set(key,quote);}
      if (!quote.pending && Date.now()-quote.at >= 30000) {
        quote.pending = true;
        this.quoteClient.price(ids.map(id=>({id,quantity:1})), {force:true}).then(value=>{
          quote.value=value;quote.failed=false;
        }).catch(()=>{
          quote.value=null;quote.failed=true;
        }).finally(()=>{
          quote.at=Date.now();quote.pending=false;this.schedule();
        });
      }
      return quote;
    }

    revalidateQuote() {
      if (!this.live) return;
      for (const quote of this.quotes.values()) quote.at=0;
      this.schedule();
    }

    render() {
      if (!this.element.isConnected) {this.observer.disconnect(); instances.delete(this); return;}
      if (this.element.dataset.useCartFinalPrice !== 'true') return;
      const currency = this.element.dataset.cartCurrency;
      let formatter;
      try {formatter = new Intl.NumberFormat(document.documentElement.lang || 'es', {style:'currency', currency});}
      catch {return;}
      const money = value => formatter.format(value / 100);
      const buffer = document.createElement('template');
      for (const label of this.element.querySelectorAll('label.quantity-break')) {
        const ids = this.selectedIds(label);
        const values = offerValues(this.variants, ids, label.dataset.compareMode);
        if (!values) continue;
        const rawConfigured = label.dataset.configuredFinalPrice;
        const configured = /^\d+$/.test(rawConfigured || '') ? Number(rawConfigured) : null;
        const cartTotal = this.cart?.currency === currency ? cartOfferTotal(this.cart, ids, this.variants.map(variant => variant.id)) : null;
        const quote = this.live ? this.liveQuote(ids) : null;
        const price = this.live ? (quote.value ? quote.value.amount : cartTotal) : (configured !== null ? configured : (cartTotal === null ? values.price : cartTotal));
        const quantity = ids.length;
        const replacements = {
          '[quantity]':String(quantity), '[price]':price === null ? (quote.failed ? 'Precio final en el carrito' : 'Calculando…') : money(price), '[price_each]':price === null ? '—' : money(price / quantity),
          '[compare_price]':money(values.compare), '[compare_price_each]':money(values.compare / quantity),
          '[amount_saved]':price === null ? '—' : money(values.compare - price), '[amount_saved_rounded]':price === null ? '—' : money(Math.round((values.compare - price) / 100) * 100),
        };
        for (const node of label.querySelectorAll('[data-text].variant-price-update')) {
          const output = node.dataset.text.replace(/\[(?:quantity|price|price_each|compare_price|compare_price_each|amount_saved|amount_saved_rounded)\]/g, key => replacements[key]);
          buffer.innerHTML = output;
          if (node.innerHTML !== buffer.innerHTML) node.innerHTML = buffer.innerHTML;
        }
        label.querySelector('.quantity-break__compare-price')?.classList.toggle('hidden', price !== null && values.compare <= price);
        label.dataset.cartPriceSource = this.live ? (quote.value ? 'live-shopify' : (cartTotal === null ? 'pending' : 'cart')) : (configured !== null ? 'configured' : (cartTotal === null ? 'catalogue' : 'cart'));
        label.dataset.cartPriceCurrency = currency;
      }
    }
  }

  function mount() {
    for (const element of document.querySelectorAll('quantity-breaks[data-use-cart-final-price="true"]')) {
      if ([...instances].some(instance => instance.element === element)) continue;
      try {instances.add(new CartPrices(element));} catch { /* Keep Shopify's normal price if initial cart data is unavailable. */ }
    }
    for (const instance of instances) {
      if (!instance.element.isConnected) {instance.observer.disconnect(); instances.delete(instance);}
    }
  }

  function refresh() {
    const version = ++requestVersion;
    clearTimeout(refreshTimer);
    for (const instance of instances) instance.revalidateQuote();
    // Keep the last valid state while reading; clearing it causes 15/21 price flashes.
    refreshTimer = setTimeout(async () => {
      const first = [...instances].find(instance => instance.element.isConnected);
      if (!first) return;
      try {
        const url = new URL(first.element.dataset.cartPricesUrl, window.location.origin);
        if (url.origin !== window.location.origin) return;
        // application/json makes Shopify bypass the Liquid view and omit order-discount allocations.
        const response = await fetch(url, {credentials:'same-origin', cache:'no-store', headers:{Accept:'text/html'}});
        if (!response.ok) return;
        const cart = await response.json();
        if (version !== requestVersion || !Array.isArray(cart.items) || typeof cart.currency !== 'string') return;
        latestCart = cart;
        for (const instance of instances) {
          if (instance.element.dataset.cartCurrency === cart.currency) {instance.cart = cart; instance.schedule();}
        }
      } catch { /* Leave the configured price or the last valid state intact. */ }
    }, 80);
  }

  function start() {
    for (const element of document.querySelectorAll('quantity-breaks[data-guide-pack-ids]')) synchronizeGuideSelection(element);
    document.addEventListener('change', event => {
      const element = event.target.closest?.('quantity-breaks[data-guide-pack-ids]');
      if (element) synchronizeGuideSelection(element);
    });
    document.addEventListener('submit', event => {
      for (const element of document.querySelectorAll('quantity-breaks[data-guide-pack-ids]')) {
        if (document.getElementById('product-form-' + element.dataset.section) === event.target) synchronizeGuideSelection(element);
      }
    }, true);
    mount();
    let mounting = false;
    new MutationObserver(records => {
      // Mount newly rendered selectors without treating unrelated cart animations as updates.
      if (!mounting && records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('quantity-breaks') || node.querySelector?.('quantity-breaks'))))) {
        mounting = true;
        queueMicrotask(() => {mounting = false; mount();});
      }
    }).observe(document.body, {childList:true, subtree:true});
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    }
    for (const event of ['cart:updated', 'cart:refresh', 'cart:change', 'cart:changed', 'shopify:section:load']) document.addEventListener(event, refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', () => {if (!document.hidden) refresh();});
    setInterval(() => {
      if (!document.hidden) for (const instance of instances) {
        if (instance.element.isConnected && instance.live) instance.revalidateQuote();
      }
    }, 30000);
    // Read the visitor's current cart even when the product page arrived from cached navigation.
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
